import type { 
    BalanceItem, 
    Organization, 
    Account, 
    BalanceFilters, 
    AccountType 
} from '../types/balances.types';
import { TYPE_COLORS, TYPE_LABELS, formatMoney } from '../types/balances.types';

// Генерация аватара из названия организации
function generateAvatar(name: string): { letter: string; gradient: string } {
    const letter = name.charAt(0).toUpperCase();
    const colors = [
        'from-blue-500 to-indigo-500',
        'from-emerald-500 to-teal-500',
        'from-amber-500 to-orange-500',
        'from-rose-500 to-pink-500',
        'from-violet-500 to-purple-500',
        'from-cyan-500 to-sky-500',
    ];
    const hash = name.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    return { letter, gradient: colors[hash % colors.length] };
}

// Получение цветов по типу счета
export function getTypeColors(type: AccountType) {
    return TYPE_COLORS[type];
}

// Получение метки по типу счета
export function getTypeLabel(type: AccountType, variant: 'short' | 'full' = 'full') {
    return TYPE_LABELS[type][variant];
}

// Группировка балансов по организациям
export function groupBalances(items: BalanceItem[]): Organization[] {
    const orgMap = new Map<string, Organization>();

    for (const item of items) {
        const orgId = item.organization.toLowerCase().replace(/\\s+/g, '-');
        const accountId = `${orgId}|${item.accountName}|${item.currency}`;

        if (!orgMap.has(orgId)) {
            const avatar = generateAvatar(item.organization);
            orgMap.set(orgId, {
                id: orgId,
                name: item.organization,
                avatar: avatar.letter,
                avatarGradient: avatar.gradient,
                accounts: [],
                totalByCurrency: {},
                totalByAccountName: { cash: 0, noncash: 0 },
            });
        }

        const org = orgMap.get(orgId)!;

        const existingIndex = org.accounts.findIndex((a: Account) => a.id === accountId);
        
        if (existingIndex >= 0) {
            const existing = org.accounts[existingIndex];
            if (new Date(item.syncedAt) > new Date(existing.syncedAt)) {
                org.totalByCurrency[item.currency] = (org.totalByCurrency[item.currency] ?? 0) - existing.amount;
                org.totalByAccountName[existing.accountName] -= existing.amount;
                
                existing.amount = item.amount;
                existing.syncedAt = item.syncedAt;
                
                org.totalByCurrency[item.currency] = (org.totalByCurrency[item.currency] ?? 0) + item.amount;
                org.totalByAccountName[item.accountName] = (org.totalByAccountName[item.accountName] ?? 0) + item.amount;
            }
        } else {
            const newAccount: Account = {
                id: accountId,
                accountName: item.accountName,
                currency: item.currency,
                amount: item.amount,
                syncedAt: item.syncedAt,
            };
            
            org.accounts.push(newAccount);
            
            org.totalByCurrency[item.currency] = (org.totalByCurrency[item.currency] ?? 0) + item.amount;
            org.totalByAccountName[item.accountName] = (org.totalByAccountName[item.accountName] ?? 0) + item.amount;
        }
    }

    return Array.from(orgMap.values()).sort((a, b) => {
        const sumA = a.totalByAccountName.cash + a.totalByAccountName.noncash;
        const sumB = b.totalByAccountName.cash + b.totalByAccountName.noncash;
        return sumB - sumA || a.name.localeCompare(b.name);
    });
}

// Фильтрация организаций
export function filterOrganizations(
    orgs: Organization[],
    filters: BalanceFilters
): Organization[] {
    return orgs
        .map(org => {
            const filteredAccounts = org.accounts.filter((acc: Account) => {
                if (filters.type !== 'all' && acc.accountName !== filters.type) return false;
                if (filters.currency !== 'all' && acc.currency !== filters.currency) return false;
                return true;
            });

            if (filteredAccounts.length === 0) return null;

            const totalByCurrency: Record<string, number> = {};
            const totalByAccountName: Record<AccountType, number> = { cash: 0, noncash: 0 };
            
            for (const acc of filteredAccounts) {
                totalByCurrency[acc.currency] = (totalByCurrency[acc.currency] ?? 0) + acc.amount;
                totalByAccountName[acc.accountName] += acc.amount;
            }

            return {
                ...org,
                accounts: filteredAccounts,
                totalByCurrency,
                totalByAccountName,
            };
        })
        .filter((org): org is Organization => org !== null)
        .filter(org => {
            if (!filters.search) return true;
            const search = filters.search.toLowerCase();
            return org.name.toLowerCase().includes(search);
        });
}

// Расчет сводной статистики
export function calculateSummary(orgs: Organization[]) {
    const byCurrency: Record<string, { cash: number; noncash: number }> = {};
    let totalCash = 0;
    let totalNoncash = 0;

    for (const org of orgs) {
        for (const acc of org.accounts) {
            if (!byCurrency[acc.currency]) {
                byCurrency[acc.currency] = { cash: 0, noncash: 0 };
            }
            byCurrency[acc.currency][acc.accountName] += acc.amount;
            
            if (acc.accountName === 'cash') totalCash += acc.amount;
            else totalNoncash += acc.amount;
        }
    }

    return {
        byCurrency,
        totalCash,
        totalNoncash,
        grandTotalRub: totalCash + totalNoncash,
    };
}

// Переэкспорт formatMoney для обратной совместимости
export { formatMoney };